import { FormState } from '../components/SampleSizeCalculator/types';

/**
 * Serializes calculator form state to URL parameters
 * @param state Form state to serialize
 * @returns URL query string (without the leading ?)
 */
export const serializeStateToUrl = (state: FormState): string => {
  const params = new URLSearchParams();
  
  // Add all form state properties to URL params
  params.set('testType', state.testType);
  params.set('variants', state.variants.toString());
  params.set('baselineValue', state.baselineValue.toString());
  params.set('standardDeviation', state.standardDeviation.toString());
  params.set('logTransformation', state.logTransformation.toString());
  params.set('mde', state.mde.toString());
  params.set('confidenceLevel', state.confidenceLevel.toString());
  params.set('power', state.power.toString());
  params.set('dailySamples', state.dailySamples.toString());
  
  return params.toString();
};

/**
 * Creates a full URL with the current calculator state
 * @param state Form state to encode in the URL
 * @returns Complete URL including the current origin and pathname
 */
export const createShareUrl = (state: FormState): string => {
  const baseUrl = `${window.location.origin}${window.location.pathname}`;
  const queryString = serializeStateToUrl(state);
  return `${baseUrl}?${queryString}`;
};

/**
 * Deserializes URL parameters to calculator form state
 * @returns Partial form state with values from URL params
 */
export const deserializeUrlToState = (): Partial<FormState> | null => {
  // Only run in browser environment
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  if (params.size === 0) return null;

  const state: Partial<FormState> = {};
  
  // Parse testType
  const testType = params.get('testType');
  if (testType === 'conversion' || testType === 'metric') {
    state.testType = testType;
  }
  
  // Parse numeric values
  const numericFields: Array<keyof FormState> = [
    'variants', 
    'baselineValue', 
    'standardDeviation', 
    'mde', 
    'confidenceLevel', 
    'power', 
    'dailySamples'
  ];
  
  numericFields.forEach(field => {
    const value = params.get(field);
    if (value !== null) {
      // @ts-ignore: Type 'string' is not assignable to type 'never'
      state[field] = parseFloat(value);
    }
  });
  
  // Parse boolean value
  const logTransformation = params.get('logTransformation');
  if (logTransformation !== null) {
    state.logTransformation = logTransformation === 'true';
  }
  
  return Object.keys(state).length > 0 ? state : null;
}; 